/**
 * Calendar.
 *
 * Built against taskdojo-app/src/screens/CalendarScreen.js.
 *
 * The structure is the phone's: a month grid whose cells carry coloured dots,
 * three tappable figures under it (Today / Overdue / This Week) that expand
 * into the list behind them, the selected day's tasks and habits, and what is
 * coming in the next fortnight.
 *
 * Two rules from that screen are worth restating because they are easy to
 * undo:
 *
 *  - it shows everything. Completed and watched tasks used to sit behind two
 *    header toggles, and hiding half a calendar by default -- behind controls
 *    whose effect is invisible on an empty day -- is the opposite of what the
 *    screen is for. The other screens filter; this one shows.
 *  - a task appears on every day it touches. One with a start and an end spans
 *    the range, and a deadline puts it on that day too, so a task can legibly
 *    appear more than once.
 *
 * Dates are the reason this screen needed care. The phone formats with an
 * unpinned locale in four separate places here -- the month label, the day
 * label, and both task formatters -- so a single screen renders up to four
 * different conventions depending on the device. Everything here goes through
 * ../../utils/datetime, which is pinned to the same en-US the rest of the app
 * uses. Day keys are built locally, never through toISOString.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useConnections } from '../../contexts/ConnectionsContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, SectionTitle, raised, pressable } from '../dojo';
import { dayKey } from '../../utils/score';
import { chipText, readableOn, MARK_MIN } from '../../utils/taskColor';
import {
  formatMonthYear, formatDayLong, formatDateFull, formatShortDateTime,
} from '../../utils/datetime';
import { toggleHabitCompletion } from '../../services/habitService';
import type { Task, Habit } from '../../services/firebaseService';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/** The phone's STATUS_COLORS, value for value. */
const STATUS_COLORS: Record<string, string> = {
  not_started: '#8E8E93',
  in_progress: '#4A6FA5',
  paused: '#C1763C',
  completed: '#4C9A2A',
  pending: '#C1763C',
  accepted: '#4A6FA5',
  declined: '#E34234',
};

const statusText = (s?: string) =>
  (s || 'not_started').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const isDone = (t: any) => !!(t?.completed || t?.status === 'completed');

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

type StatKey = 'today' | 'overdue' | 'thisWeek';

const Calendar: React.FC = () => {
  const { state } = useApp();
  const { connections } = useConnections();
  const { colors, isDark } = useTheme();
  /** The surface marks and chips sit on, which is what contrast is measured against. */
  const card = isDark ? '#1B2A32' : '#FFFFFF';


  const uid = state.user?.uid;
  const tasks = (state.tasks || []) as Task[];
  const habits = (state.habits || []) as Habit[];

  const today = useMemo(() => new Date(), []);
  const [viewDate, setViewDate] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [expandedStat, setExpandedStat] = useState<StatKey | null>(null);
  const [busyHabit, setBusyHabit] = useState<string | null>(null);
  const [error, setError] = useState('');

  const nameFor = useCallback((id?: string) => {
    if (!id) return '';
    if (id === uid) return 'Me';
    const c = connections.find((x: any) => x.uid === id);
    return c?.displayName || c?.email || id.slice(0, 8);
  }, [connections, uid]);

  /** Six weeks of cells, padded from the neighbouring months. */
  const grid = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const startDow = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells: Array<{ date: Date; day: number; inMonth: boolean; isToday: boolean }> = [];

    for (let i = 0; i < startDow; i += 1) {
      const d = new Date(year, month, -startDow + i + 1);
      cells.push({ date: d, day: d.getDate(), inMonth: false, isToday: false });
    }
    for (let d = 1; d <= daysInMonth; d += 1) {
      const date = new Date(year, month, d);
      cells.push({ date, day: d, inMonth: true, isToday: sameDay(date, today) });
    }
    const remaining = 7 - (cells.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i += 1) {
        const d = new Date(year, month + 1, i);
        cells.push({ date: d, day: d.getDate(), inMonth: false, isToday: false });
      }
    }
    return cells;
  }, [viewDate, today]);

  /**
   * Every task, on every day it touches. A start/end pair fills the range; a
   * deadline adds that day. Keyed locally, so a 9pm deadline in London does not
   * land on tomorrow.
   */
  const tasksByDate = useMemo(() => {
    const map = new Map<string, Task[]>();
    const add = (key: string, task: Task) => {
      const arr = map.get(key) || [];
      if (!arr.some((t) => t.id === task.id)) arr.push(task);
      map.set(key, arr);
    };

    tasks.forEach((task) => {
      if (task.startDateTime && task.endDateTime) {
        const start = new Date(task.startDateTime);
        const end = new Date(task.endDateTime);
        if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
          const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
          const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
          // Guarded: an end before its start would otherwise never terminate.
          let guard = 0;
          while (cursor <= last && guard < 400) {
            add(dayKey(cursor), task);
            cursor.setDate(cursor.getDate() + 1);
            guard += 1;
          }
        }
      } else if (task.startDateTime) {
        const d = new Date(task.startDateTime);
        if (!Number.isNaN(d.getTime())) add(dayKey(d), task);
      }

      if (task.deadline) {
        const d = new Date(task.deadline);
        if (!Number.isNaN(d.getTime())) add(dayKey(d), task);
      }
    });

    return map;
  }, [tasks]);

  const dotsFor = useCallback((date: Date) => {
    const dateTasks = tasksByDate.get(dayKey(date)) || [];
    const out: string[] = [];
    dateTasks.forEach((t) => {
      const c = t.color || '#8E5572';
      if (!out.includes(c) && out.length < 3) out.push(c);
    });
    // Habits are daily, so any day gets a habit dot once habits exist.
    if (habits.length > 0 && out.length < 4) out.push('#E34234');
    return out;
  }, [tasksByDate, habits.length]);

  const stats = useMemo(() => {
    const now = new Date();
    const weekEnd = new Date(now);
    weekEnd.setDate(weekEnd.getDate() + 7);

    const overdueTasks = tasks.filter(
      (t) => !isDone(t) && t.deadline && new Date(t.deadline) < now,
    );
    const thisWeekTasks = tasks.filter((t) => {
      if (isDone(t) || !t.deadline) return false;
      const d = new Date(t.deadline);
      return d >= now && d <= weekEnd;
    });
    const todayTasks = tasksByDate.get(dayKey(now)) || [];

    return { todayTasks, overdueTasks, thisWeekTasks };
  }, [tasks, tasksByDate]);

  const upcoming = useMemo(() => {
    const now = new Date();
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() + 14);
    return tasks
      .filter((t) => {
        if (isDone(t)) return false;
        const raw = t.deadline || t.startDateTime;
        if (!raw) return false;
        const d = new Date(raw);
        return !Number.isNaN(d.getTime()) && d >= now && d <= cutoff;
      })
      .sort((a, b) =>
        new Date(a.deadline || a.startDateTime || 0).getTime()
        - new Date(b.deadline || b.startDateTime || 0).getTime())
      .slice(0, 8);
  }, [tasks]);

  const selectedKey = dayKey(selectedDate);
  const selectedTasks = tasksByDate.get(selectedKey) || [];
  const doneOnSelected = (state.completions?.[selectedKey] as any)?.habits || [];

  const toggleHabit = async (habit: Habit) => {
    if (!uid || busyHabit) return;
    setBusyHabit(habit.id);
    setError('');
    try {
      // The selected date is passed through, so a habit can be ticked for a day
      // other than today -- which is the whole reason to do it from a calendar.
      await toggleHabitCompletion(habit.id, uid, selectedDate);
    } catch (e: any) {
      setError(e?.message || "Couldn't save that. Please try again.");
    } finally {
      setBusyHabit(null);
    }
  };

  const shiftMonth = (delta: number) =>
    setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + delta, 1));

  const goToToday = () => {
    const now = new Date();
    setViewDate(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedDate(now);
  };

  // Every pressable control in the app carries the two shaded faces -- heavier
  // bottom and right -- so it reads as a key rather than a flat tile.
  const arrow = `w-9 h-9 rounded-[12px] flex items-center justify-center ${raised} ${pressable}
    ${colors.textSecondary} ${colors.cardBorder}
    ${isDark ? 'bg-dojo-surfaceDeep hover:bg-slate-700' : 'bg-stone-100 hover:bg-stone-200'}`;

  const statTiles: Array<{ key: StatKey; label: string; value: number; color?: string }> = [
    { key: 'today', label: 'Today', value: stats.todayTasks.length },
    { key: 'overdue', label: 'Overdue', value: stats.overdueTasks.length, color: stats.overdueTasks.length > 0 ? '#E34234' : undefined },
    { key: 'thisWeek', label: 'This week', value: stats.thisWeekTasks.length, color: '#4A6FA5' },
  ];

  const expandedList =
    expandedStat === 'today' ? stats.todayTasks
      : expandedStat === 'overdue' ? stats.overdueTasks
        : expandedStat === 'thisWeek' ? stats.thisWeekTasks
          : [];

  /** A task, as the day list and the upcoming list both draw it. */
  const TaskRow: React.FC<{ task: Task; onClick?: () => void }> = ({ task, onClick }) => {
    const color = STATUS_COLORS[task.status] || '#8E8E93';
    const overdue = !!task.deadline && new Date(task.deadline) < new Date() && !isDone(task);

    return (
      <div
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick}
        onKeyDown={(e) => {
          if (onClick && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); }
        }}
        className={`rounded-[15px] border-2 border-b-[3px] ${colors.card} ${colors.cardBorder}
          flex items-stretch overflow-hidden ${onClick ? 'cursor-pointer' : ''}
          ${onClick && isDark ? 'hover:border-slate-500' : ''}
          ${onClick && !isDark ? 'hover:border-stone-400' : ''}`}
      >
        <span className="w-1.5 flex-shrink-0"
          style={{ backgroundColor: readableOn(task.color || '#8E5572', card, MARK_MIN) }} />
        <div className="flex-1 min-w-0 px-3.5 py-3">
          <h3 className={`text-[15px] font-extrabold tracking-[-0.2px] break-words
            ${isDone(task) ? `line-through ${colors.textSecondary}` : ''}`}>
            {task.title}
          </h3>

          <div className="mt-1.5">
            <span
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full
                text-[11px] font-extrabold uppercase tracking-[0.4px]"
              style={{ color: chipText(color, card), backgroundColor: `${color}20` }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
              {statusText(task.status)}
            </span>
          </div>

          {task.startDateTime && (
            <p className={`text-[12px] mt-1.5 ${colors.textSecondary}`}>
              {formatShortDateTime(task.startDateTime)}
              {task.endDateTime ? ` → ${formatShortDateTime(task.endDateTime)}` : ''}
            </p>
          )}
          {task.deadline && (
            <p className={`text-[12px] mt-0.5 ${overdue ? 'text-dojo-red font-semibold' : colors.textSecondary}`}>
              Deadline: {formatDateFull(task.deadline)}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
            {task.assignedTo && task.assignedTo !== task.createdBy && (
              <span className="text-[12px] font-semibold text-dojo-plum">
                → {nameFor(task.assignedTo)}
              </span>
            )}
            {task.category && (
              <span className={`text-[12px] ${colors.textSecondary}`}>{task.category}</span>
            )}
            {task.tag && <span className="text-[12px] text-dojo-blue">#{task.tag}</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${colors.bg} ${colors.text}`}>
      <div className="max-w-3xl mx-auto pt-4 pb-24">
        {/* The phone's header for this screen: title, and Today as the action. */}
        <div className="flex items-center gap-3 px-5 pt-2 pb-4">
          <h1 className={`text-[30px] sm:text-[34px] font-black tracking-[-0.9px] flex-1 ${colors.text}`}>
            Calendar
          </h1>
          <button
            type="button"
            onClick={goToToday}
            className={`rounded-[13px] bg-dojo-red border-dojo-redDeep text-white ${raised} ${pressable}
              px-[15px] pt-[9px] pb-[7px] text-[14px] font-extrabold`}
          >
            Today
          </button>
        </div>

        {error && (
          <div role="alert" className="mx-5 mb-4 rounded-[14px] border-2 border-dojo-red/50 bg-dojo-red/10
            px-4 py-3 text-[13px] font-semibold text-dojo-red">
            {error}
          </div>
        )}

        <div className="px-5 space-y-5">
          {/* ------------------------------ the month ------------------------------ */}
          <Card className="p-4 !rounded-[18px]">
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-[20px] font-black tracking-[-0.4px] ${colors.text}`}>
                {formatMonthYear(viewDate)}
              </h2>
              <div className="flex gap-2">
                <button type="button" onClick={() => shiftMonth(-1)} aria-label="Previous month" className={arrow}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button type="button" onClick={() => shiftMonth(1)} aria-label="Next month" className={arrow}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 mb-2">
              {WEEKDAYS.map((d, i) => (
                // Two of these are "T" and two are "S", so the index has to
                // carry the key -- the letter is not unique.
                <span key={i} className={`text-center text-[12px] font-extrabold ${colors.textSecondary}`}>
                  {d}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-7">
              {grid.map((cell, i) => {
                const dots = dotsFor(cell.date);
                const selected = sameDay(cell.date, selectedDate);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelectedDate(new Date(cell.date))}
                    aria-label={formatDayLong(cell.date)}
                    aria-pressed={selected}
                    // Square on a phone, as on the device. On a wide screen a
                    // square cell is ~100px and the month swallows the page, so
                    // it takes a fixed height there instead.
                    className={`aspect-square sm:aspect-auto sm:h-[62px]
                      rounded-[14px] border-2 flex flex-col items-center justify-center
                      gap-[3px] transition-colors
                      ${cell.isToday ? 'border-dojo-red' : 'border-transparent'}
                      ${!cell.isToday && !selected && (isDark ? 'hover:bg-dojo-surfaceDeep' : 'hover:bg-stone-100')}`}
                    style={selected && !cell.isToday
                      ? { backgroundColor: isDark ? 'rgba(227,66,52,0.15)' : 'rgba(227,66,52,0.10)' }
                      : undefined}
                  >
                    <span
                      className={`text-[13px] font-extrabold tabular-nums
                        ${cell.inMonth ? colors.text : `${colors.textSecondary} opacity-40`}`}
                    >
                      {cell.day}
                    </span>
                    <span className="flex gap-[2px] h-[6px]">
                      {dots.map((c, j) => (
                        <span key={j} className="w-[6px] h-[6px] rounded-full block"
                          style={{ backgroundColor: c }} />
                      ))}
                    </span>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* ------------------------------- figures ------------------------------- */}
          <div className="grid grid-cols-3 gap-3">
            {statTiles.map((s) => {
              const open = expandedStat === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setExpandedStat(open ? null : s.key)}
                  aria-expanded={open}
                  className={`rounded-[18px] border-2 border-b-[3px] px-3 py-3.5 flex flex-col items-center
                    transition-colors ${colors.card}
                    ${open ? 'border-dojo-red' : colors.cardBorder}`}
                >
                  <span
                    className="text-[26px] font-black tracking-[-0.8px] tabular-nums leading-none"
                    style={s.color ? { color: s.color } : undefined}
                  >
                    {s.value}
                  </span>
                  <span className={`text-[12px] font-bold mt-1 ${colors.textSecondary}`}>{s.label}</span>
                </button>
              );
            })}
          </div>

          {expandedStat && (
            <div>
              <SectionTitle
                className="mb-3"
                right={
                  <button
                    type="button"
                    onClick={() => setExpandedStat(null)}
                    aria-label="Close"
                    className={colors.textSecondary}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round" />
                    </svg>
                  </button>
                }
              >
                {statTiles.find((s) => s.key === expandedStat)?.label}
              </SectionTitle>

              {expandedList.length === 0 ? (
                <Card className="p-6 flex flex-col items-center">
                  <p className={`text-[14px] font-semibold ${colors.textSecondary}`}>Nothing here.</p>
                </Card>
              ) : (
                <div className="space-y-2.5">
                  {expandedList.map((t) => <TaskRow key={t.id} task={t} />)}
                </div>
              )}
            </div>
          )}

          {/* ---------------------------- the chosen day ---------------------------- */}
          <div>
            <h2 className={`text-[17px] font-extrabold tracking-[-0.3px] ${colors.text}`}>
              {formatDayLong(selectedDate)}
            </h2>
            <p className={`text-[13px] font-semibold mt-0.5 mb-3 ${colors.textSecondary}`}>
              {selectedTasks.length} task{selectedTasks.length !== 1 ? 's' : ''}
              {habits.length > 0 && ` · ${habits.length} habit${habits.length !== 1 ? 's' : ''}`}
            </p>

            {selectedTasks.length === 0 && habits.length === 0 ? (
              <Card className="p-6 flex flex-col items-center gap-2">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="1.8" className={colors.textMuted}>
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" />
                </svg>
                <p className={`text-[14px] font-semibold ${colors.textSecondary}`}>
                  Nothing scheduled for this day
                </p>
              </Card>
            ) : (
              <div className="space-y-2.5">
                {selectedTasks.map((t) => <TaskRow key={t.id} task={t} />)}

                {habits.length > 0 && (
                  <div className="pt-2">
                    <p className={`text-[11px] font-bold uppercase tracking-[0.6px] mb-2 ${colors.textMuted}`}>
                      Habits
                    </p>
                    <div className="space-y-2">
                      {habits.map((habit) => {
                        const done = doneOnSelected.includes(habit.id);
                        const working = busyHabit === habit.id;
                        return (
                          <button
                            key={habit.id}
                            type="button"
                            onClick={() => toggleHabit(habit)}
                            disabled={working}
                            aria-pressed={done}
                            className={`w-full rounded-[15px] border-2 border-b-[3px] ${colors.card}
                              ${colors.cardBorder} flex items-stretch overflow-hidden text-left
                              disabled:opacity-50 ${pressable}`}
                          >
                            <span className="w-1.5 flex-shrink-0"
                              style={{ backgroundColor: readableOn(habit.color || '#E34234', card, MARK_MIN) }} />
                            <span className="flex-1 min-w-0 px-3.5 py-3">
                              <span className="flex items-center gap-2.5">
                                <span className="text-[17px] leading-none">{habit.icon || '🔥'}</span>
                                <span className={`flex-1 min-w-0 text-[15px] font-extrabold truncate
                                  ${done ? `line-through ${colors.textSecondary}` : ''}`}>
                                  {habit.title}
                                </span>
                                {done ? (
                                  <svg width="22" height="22" viewBox="0 0 24 24" fill="#4C9A2A" className="flex-shrink-0">
                                    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.2 14.2l-4-4 1.4-1.4 2.6 2.6 5.6-5.6 1.4 1.4z" />
                                  </svg>
                                ) : (
                                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    strokeWidth="1.8" className={`flex-shrink-0 ${colors.textMuted}`}>
                                    <circle cx="12" cy="12" r="10" />
                                  </svg>
                                )}
                              </span>
                              {habit.streak > 0 && (
                                <span className={`block text-[12px] mt-1 ${colors.textSecondary}`}>
                                  🔥 {habit.streak} day streak
                                </span>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ------------------------------- upcoming ------------------------------- */}
          {upcoming.length > 0 && (
            <div>
              <SectionTitle className="mb-3">Upcoming</SectionTitle>
              <div className="space-y-2.5">
                {upcoming.map((t) => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    onClick={() => {
                      // Selecting it moves the calendar to that day, as on the
                      // phone, so the grid and the list stay in step.
                      const raw = t.deadline || t.startDateTime;
                      if (!raw) return;
                      const d = new Date(raw);
                      if (Number.isNaN(d.getTime())) return;
                      setSelectedDate(d);
                      setViewDate(new Date(d.getFullYear(), d.getMonth(), 1));
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Calendar;
